// src/services/cookieService.ts
import Cookies from "js-cookie";

export interface CookieOptions {
  expires?: number | Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
  httpOnly?: boolean;
}

/** Cookie 服务：按系统前缀隔离，统一选项，跨系统无冲突 */
class CookieService {
  private systemPrefix: string;
  private basePath: string;
  private defaultOptions: Cookies.CookieAttributes;

  constructor() {
    this.systemPrefix = this.extractSystemPrefix();
    this.basePath = this.getBasePath();
    this.defaultOptions = this.createDefaultOptions();
  }

  /** 提取系统前缀，优先级：VITE_SYSTEM_CONFIG > default */
  private extractSystemPrefix(): string {
    // 优先使用 VITE_SYSTEM_CONFIG
    const systemConfig = import.meta.env.VITE_SYSTEM_CONFIG;
    if (systemConfig && systemConfig.trim()) {
      return systemConfig.trim();
    }

    // 如果没有 VITE_SYSTEM_CONFIG，直接返回空字符串（不使用前缀）
    return "";
  }

  private getBasePath(): string {
    return "/";
  }

  /** 创建默认 Cookie 选项 */
  private createDefaultOptions(): Cookies.CookieAttributes {
    return {
      path: this.basePath,
      secure: window.location.protocol === "https:",
      sameSite: "Lax" as const,
    };
  }

  /** 生成带前缀的内部 Cookie 名称 */
  private getInternalName(name: string): string {
    return this.systemPrefix ? `${this.systemPrefix}_${name}` : name;
  }

  /** 获取后端发送用的 Cookie 名称（无前缀） */
  public getBackendName(name: string): string {
    return name;
  }

  /** 格式化基础路径，确保前后都有斜杠 */
  private normalizeBasePath(basePath: string): string {
    if (!basePath || basePath === "/") {
      return "/";
    }

    // 确保以 / 开头
    if (!basePath.startsWith("/")) {
      basePath = "/" + basePath;
    }

    // 确保以 / 结尾（目录路径）
    if (!basePath.endsWith("/")) {
      basePath = basePath + "/";
    }

    return basePath;
  }

  /** 获取当前系统能访问的所有路径 */
  private getAccessiblePaths(): string[] {
    const paths = new Set<string>();

    // 总是包含根路径
    paths.add("/");

    // 添加当前运行路径（经过格式化）
    const rawBaseUrl = import.meta.env.VITE_BASE_URL;
    if (rawBaseUrl) {
      const normalizedPath = this.normalizeBasePath(rawBaseUrl);
      if (normalizedPath !== "/") {
        paths.add(normalizedPath);
      }
    }

    return Array.from(paths);
  }

  /** 设置 Cookie */
  public set(name: string, value: string, options: CookieOptions = {}): void {
    const internalName = this.getInternalName(name);
    const mergedOptions = this.mergeOptions(options);

    try {
      Cookies.set(internalName, value, mergedOptions);
      console.debug(`Cookie set: ${internalName} (backend: ${name})`, {
        path: mergedOptions.path,
        expires: mergedOptions.expires,
        secure: mergedOptions.secure,
        sameSite: mergedOptions.sameSite,
      });
    } catch (error) {
      console.error(`Failed to set cookie ${internalName}:`, error);
      throw new Error(`Cookie设置失败: ${name}`);
    }
  }

  /** 获取 Cookie，找不到返回 null */
  public get(name: string): string | null {
    const internalName = this.getInternalName(name);
    const value = Cookies.get(internalName);

    console.debug(
      `Cookie get: ${internalName} (backend: ${name}) = ${value ? "[PRESENT]" : "[NOT_FOUND]"}`,
    );
    return value || null;
  }

  /** 删除 Cookie */
  public remove(name: string): void {
    const internalName = this.getInternalName(name);

    try {
      // 使用相同的路径选项进行删除
      Cookies.remove(internalName, {
        path: this.basePath,
        domain: this.defaultOptions.domain,
      });

      // 额外尝试删除可能存在的其他路径配置
      Cookies.remove(internalName, { path: "/" });
      Cookies.remove(internalName);

      console.debug(`Cookie removed: ${internalName} (backend: ${name})`);
    } catch (error) {
      console.error(`Failed to remove cookie ${internalName}:`, error);
    }
  }

  /** 检查 Cookie 是否存在 */
  public exists(name: string): boolean {
    return this.get(name) !== null;
  }

  /** 强制清除指定路径下的系统Cookie */
  private clearCookiesAtPath(path: string): void {
    if (!this.systemPrefix) {
      // 当没有系统前缀时，清除标准Cookie
      const standardCookies = ["jwt_token", "refresh_token"];
      standardCookies.forEach((cookieName) => {
        try {
          // 使用过期时间强制删除
          Cookies.set(cookieName, "", {
            path: path,
            expires: new Date(0),
          });
          Cookies.remove(cookieName, { path: path });
        } catch (error) {
          console.debug(
            `Failed to clear cookie ${cookieName} at path ${path}:`,
            error,
          );
        }
      });
      return;
    }

    // 有系统前缀时，清除带前缀的Cookie
    const standardCookies = ["jwt_token", "refresh_token"];
    standardCookies.forEach((cookieName) => {
      const internalName = this.getInternalName(cookieName);
      try {
        // 使用过期时间强制删除
        Cookies.set(internalName, "", {
          path: path,
          expires: new Date(0),
        });
        Cookies.remove(internalName, { path: path });
      } catch (error) {
        console.debug(
          `Failed to clear cookie ${internalName} at path ${path}:`,
          error,
        );
      }
    });
  }

  /** 强制清理所有可访问路径下的系统Cookie */
  public clearAllAccessiblePaths(): void {
    const accessiblePaths = this.getAccessiblePaths();

    console.debug(
      `Clearing cookies from all accessible paths: ${accessiblePaths.join(", ")}`,
    );

    accessiblePaths.forEach((path) => {
      this.clearCookiesAtPath(path);
    });

    console.debug(
      `Cleared cookies from ${accessiblePaths.length} accessible paths`,
    );
  }

  /** 将Cookie从错误路径迁移到根路径（简化版本） */
  public migrateCookiesToRootPath(): void {
    const accessiblePaths = this.getAccessiblePaths();
    const standardCookies = ["jwt_token", "refresh_token"];

    console.debug(
      `Migrating cookies from paths: ${accessiblePaths.join(", ")} to root path`,
    );

    // 先尝试强制清理所有非根路径的Cookie
    accessiblePaths.forEach((path) => {
      if (path !== "/") {
        this.clearCookiesAtPath(path);
      }
    });

    console.debug(
      "Cookie path migration completed - cleared all non-root path cookies",
    );
  }

  /** 清除当前系统的所有 Cookie */
  public clearAll(): void {
    // 使用新的强制清理方法，清理所有可访问路径下的Cookie
    this.clearAllAccessiblePaths();

    if (!this.systemPrefix) {
      console.debug(
        `Cleared standard cookies from all accessible paths (no system prefix)`,
      );
      return;
    }

    // 额外清理：尝试清理可能遗留的其他Cookie
    const allCookies = document.cookie.split(";");
    const systemCookies = allCookies
      .map((cookie) => cookie.trim().split("=")[0])
      .filter((cookieName) => cookieName.startsWith(`${this.systemPrefix}_`));

    systemCookies.forEach((cookieName) => {
      // 提取后端名称并删除
      const backendName = cookieName.replace(`${this.systemPrefix}_`, "");
      this.remove(backendName);
    });

    console.debug(
      `Cleared ${systemCookies.length} system cookies for system: ${this.systemPrefix}`,
    );
  }

  /** 合并 Cookie 选项 */
  private mergeOptions(options: CookieOptions): Cookies.CookieAttributes {
    return {
      ...this.defaultOptions,
      ...options,
    };
  }

  /** 获取当前系统信息（调试） */
  public getSystemInfo(): {
    prefix: string;
    basePath: string;
    defaultOptions: Cookies.CookieAttributes;
  } {
    return {
      prefix: this.systemPrefix,
      basePath: this.basePath,
      defaultOptions: this.defaultOptions,
    };
  }

  /** 列出当前系统的所有 Cookie（调试） */
  public listSystemCookies(): { [key: string]: string } {
    if (!this.systemPrefix) {
      // 当没有系统前缀时，列出所有标准 Cookie
      const standardCookies = ["jwt_token", "refresh_token"];
      const systemCookies: { [key: string]: string } = {};

      standardCookies.forEach((cookieName) => {
        const value = Cookies.get(cookieName);
        if (value) {
          systemCookies[cookieName] = value;
        }
      });

      return systemCookies;
    }

    const allCookies = document.cookie.split(";");
    const systemCookies: { [key: string]: string } = {};

    allCookies.forEach((cookie) => {
      const [name, value] = cookie.trim().split("=");
      if (name && name.startsWith(`${this.systemPrefix}_`)) {
        const backendName = name.replace(`${this.systemPrefix}_`, "");
        systemCookies[backendName] = value || "";
      }
    });

    return systemCookies;
  }
}

// 导出单例实例
export const cookieService = new CookieService();

// 导出类型和类以便测试
export { CookieService };
